// src/calculators/advanceChange.js — калкулатор „Промяна на авансовите вноски"
// (чл. 88–90 ЗКПО, ПРОМТ-22). Сметката е в services/advanceChange.js (чиста
// функция, с тестове); тук е само екранът. Пише се за човек, който не е
// счетоводител: питане с думи, отговор с няколко ясни реда с числа.
// Цитатите в колоната са сверени дословно в Ciela Net (23.10.2026).

import { el, clear, field, input } from '../shared/dom.js';
import { money } from '../shared/format.js';
import { dateInput, localTodayIso } from '../shared/dates.js';
import { withLaw, hintify, ECB_RATE_WARNING } from './law.js';
import { takeZkpoChangePrefill } from './prefill.js';
import { advanceChange } from '../../services/advanceChange.js';

const line = (label, value, big) => el('div', { class: 'calc-line' + (big ? ' big' : '') }, el('span', { text: label }), el('span', { text: value }));
const noteRow = (t) => el('div', { style: 'font-size:12px;color:var(--muted);padding:2px 0', text: '• ' + t });
const warnRow = (t) => el('div', { class: 'sev-warn', text: t });
const checkRow = (box, text) => el('div', { class: 'field' }, el('label', { class: 'choice' }, box, el('span', { text })));
const dbg = (iso) => (iso ? iso.split('-').reverse().join('.') : '');
const m = (x) => money(x, 'EUR');

export function buildAdvanceChangeCalc() {
  // „Авансови ЗКПО" подава годишния данък — взема се еднократно.
  const pre = takeZkpoChangePrefill();
  const f = {
    kind: el('select', {},
      el('option', { value: 'quarterly', text: 'Тримесечни (приходи до 3 000 000 лв. / 1 533 875,64 €)', selected: true }),
      el('option', { value: 'monthly', text: 'Месечни (над 3 000 000 лв.)' })),
    declared: input({ type: 'number', step: '0.01', min: '0', placeholder: 'една вноска' }),
    newProfit: input({ type: 'number', step: '0.01', min: '0', placeholder: 'за цялата година' }),
    rate: input({ type: 'number', step: '0.01', min: '0', value: '10' }),
    filedOn: dateInput(localTodayIso()),
    interest: input({ type: 'number', step: '0.01', min: '0', value: '8' }),
    changed: el('input', { type: 'checkbox' }),
    prevDate: dateInput(''),
    prevAmount: input({ type: 'number', step: '0.01', min: '0', placeholder: 'новата вноска тогава' }),
  };

  // Досегашната вноска от „Авансови ЗКПО": годишният данък ÷ 4 или ÷ 12.
  // Пренася се при смяна на вида само докато човекът не я е пипнал.
  const fromPre = () => (pre && pre.annualTax > 0
    ? String(Math.round(pre.annualTax / (f.kind.value === 'monthly' ? 12 : 4) * 100) / 100) : '');
  let preValue = fromPre();
  if (preValue) f.declared.value = preValue;
  f.kind.addEventListener('change', () => {
    if (preValue && f.declared.value === preValue) { preValue = fromPre(); f.declared.value = preValue; }
  });

  const prevDateField = field('Дата на подаване на предишната декларация', f.prevDate, { size: 'm' });
  const prevAmountField = field('Вноската по предишната декларация', f.prevAmount, { size: 'm' });
  const dateMsg = el('div', { class: 'sev-warn', role: 'alert' });

  const result = el('div', { class: 'calc-result' });
  const scheduleBox = el('div', { style: 'margin-top:10px' });
  const notesBox = el('div', { style: 'margin-top:8px' });

  function recompute() {
    clear(result); clear(scheduleBox); clear(notesBox);
    const changed = f.changed.checked;
    prevDateField.style.display = changed ? '' : 'none';
    prevAmountField.style.display = changed ? '' : 'none';

    const filedOn = f.filedOn.dataset.iso || '';
    const r = filedOn ? advanceChange({
      kind: f.kind.value,
      declared: Number(f.declared.value) || 0,
      newProfit: Number(f.newProfit.value) || 0,
      rate: f.rate.value === '' ? 10 : Number(f.rate.value),
      filedOn,
      year: Number(filedOn.slice(0, 4)),
      interestAnnual: Number(f.interest.value) || 0,
      changedAlready: changed && f.prevDate.dataset.iso ? { filedOn: f.prevDate.dataset.iso, amount: Number(f.prevAmount.value) || 0 } : null,
    }) : null;

    // Дата след 15.11 — полето е в червено, с текста от сметката.
    if (r && r.error) {
      f.filedOn.setAttribute('aria-invalid', 'true');
      f.filedOn.classList.add('bad');
      dateMsg.textContent = '⚠ ' + r.error;
      dateMsg.style.display = '';
    } else {
      if (f.filedOn.getAttribute('aria-invalid') && filedOn) { f.filedOn.removeAttribute('aria-invalid'); f.filedOn.classList.remove('bad'); }
      dateMsg.style.display = 'none';
    }

    if (!r || !(Number(f.newProfit.value) > 0)) {
      result.appendChild(el('div', { class: 'calc-line', text: 'Въведете досегашната вноска, новата прогнозна печалба и датата на подаване.' }));
      return;
    }
    const each = r.kind === 'monthly' ? 'всяка от оставащите месечни вноски' : 'всяка от оставащите тримесечни вноски';
    result.appendChild(line('Очакван годишен данък (печалба × ' + String(r.rate).replace('.', ',') + ' %)', m(r.B)));
    const oldText = r.paidBefore === r.nBefore * r.declared
      ? ' (' + r.nBefore + ' × ' + m(r.declared) + ')' : ' (' + r.nBefore + ' вноски)';
    result.appendChild(line('Вноски с падеж до датата на декларацията' + oldText, m(r.paidBefore)));
    result.appendChild(line('Остават вноски след датата', String(r.nAfter)));

    if (r.nAfter === 0) {
      result.appendChild(warnRow('⚠ ' + r.notes.find((n) => /Няма вноска след/.test(n))));
    } else if (r.overpaid > 0) {
      result.appendChild(line('Надвнесено', m(r.overpaid), true));
      result.appendChild(line('Нова вноска', m(0)));
    } else {
      result.appendChild(line('Нова вноска — минимум, за да няма лихва (' + each + ')', m(r.minNew), true));
      result.appendChild(line('Нова вноска, с която се внася точно данъкът', m(r.exactNew)));
    }
    result.appendChild(line('Ако не промените нищо: лихва по чл. 89', m(r.noChange.interest)
      + (r.noChange.interest > 0 ? ' (сума за лихва ' + m(r.noChange.A) + ', ' + r.noChange.days + ' дни)' : '')));
    result.appendChild(el('div', { class: 'calc-hint-line', text: ECB_RATE_WARNING }));

    // Графикът — падеж · период · сума · стара/нова.
    scheduleBox.appendChild(el('h4', { text: 'График на вноските', style: 'margin:8px 0 4px' }));
    scheduleBox.appendChild(el('table', { class: 'items' },
      el('thead', {}, el('tr', {},
        el('th', { scope: 'col', text: 'Падеж' }), el('th', { scope: 'col', text: 'За' }),
        el('th', { scope: 'col', class: 'num', text: 'Сума' }), el('th', { scope: 'col', text: 'Вноска' }))),
      el('tbody', {}, ...r.schedule.map((x) => el('tr', {},
        el('td', { text: dbg(x.due) + (x.due !== x.dueBase ? ' (вместо ' + dbg(x.dueBase) + ' — неработен ден)' : '') }),
        el('td', { text: x.period }),
        el('td', { class: 'num', text: x.amount == null ? '—' : m(x.amount) }),
        el('td', { text: x.phase === 'before' ? 'стара' : 'нова' }))))));
    r.notes.forEach((n) => { if (!/Няма вноска след/.test(n) || r.nAfter > 0) notesBox.appendChild(noteRow(n)); });
  }

  [...Object.values(f)].forEach((n) => { n.addEventListener('input', recompute); n.addEventListener('change', recompute); });

  const panel = el('div', { class: 'panel calc-panel', style: 'max-width:860px' },
    el('h3', { class: 'calc-title', text: 'Промяна на авансовите вноски по ЗКПО (чл. 88)' }),
    hintify(field('Вид на вноските', f.kind),
      'чл. 84 и 85 ЗКПО', 'Месечни — при нетни приходи от продажби над 3 000 000 лв. за годината преди предходната; останалите — тримесечни.'),
    el('div', { style: 'display:flex;gap:10px;flex-wrap:wrap' },
      hintify(field('Досегашна вноска (една вноска, както е декларирана до 15 април)', f.declared, { size: 'm' }),
        'чл. 87а, ал. 1 ЗКПО', 'Първоначалните вноски се декларират в срок от 1 март до 15 април — те са „старите" вноски до промяната.'),
      hintify(field('Нова прогнозна данъчна печалба за годината', f.newProfit, { size: 'm' }),
        'чл. 88, ал. 1 ЗКПО', 'Декларация за промяна се подава, когато вноските ще се отличават от дължимия годишен данък.')),
    el('details', { style: 'margin:4px 0 8px' },
      el('summary', { text: 'Още' }),
      hintify(field('Данъчна ставка (%)', f.rate, { size: 's' }), 'чл. 20 ЗКПО', 'Данъчната ставка на корпоративния данък е 10 на сто.')),
    el('div', { style: 'display:flex;gap:10px;flex-wrap:wrap' },
      hintify(field('Дата на подаване на декларацията по чл. 88', f.filedOn, { size: 'm' }),
        'чл. 88, ал. 2 ЗКПО', 'Намалението или увеличението важи за вноските след подаването на декларацията; срокът е 15 ноември.'),
      hintify(field('Законна лихва (%) — ЕЦБ + 8 пункта', f.interest, { size: 'm' }),
        'чл. 89, ал. 5 ЗКПО', 'Лихвата е по Закона за лихвите върху данъци, такси и други подобни държавни вземания — законната, от 16 април до 31 декември.')),
    dateMsg,
    checkRow(f.changed, 'През годината вече е подавана декларация по чл. 88'),
    el('div', { style: 'display:flex;gap:10px;flex-wrap:wrap' }, prevDateField, prevAmountField),
    result,
    scheduleBox,
    notesBox);

  recompute();

  return withLaw(panel, {
    when: [
      'Есенна проверка до 15 ноември: стигат ли внесените авансови вноски, за да няма лихва при годишната декларация.',
      'Печалбата ще е по-голяма от прогнозата през пролетта — увеличете оставащите вноски поне до минимума, за да няма лихва.',
      'Печалбата ще е по-малка — намалете вноските; ако вече е внесено повече от данъка, новите вноски могат да са 0.',
      'След 15 ноември декларация за промяна вече не се подава — остава само да се види колко е лихвата.',
    ],
    quotes: [
      { ref: 'чл. 88, ал. 1 и 2 ЗКПО', text: 'Данъчно задължените лица могат да подават в срок до 15 ноември на съответната година декларация по образец за намаляване или увеличаване на авансовите вноски, когато смятат, че те ще се отличават от дължимия годишен корпоративен данък. … Намалението, съответно увеличението на авансовите вноски се ползва след подаването на декларацията.' },
      { ref: 'чл. 89, ал. 1 ЗКПО', text: 'Когато дължимият годишен корпоративен данък надвиши с над 25 на сто сумата от определените месечни авансови вноски за съответната година или когато 75 на сто от дължимия годишен корпоративен данък надвиши с над 25 на сто сумата от определените тримесечни авансови вноски за съответната година, върху превишението над 25 на сто се дължи лихва.' },
      { ref: 'чл. 89, ал. 2 ЗКПО', text: '1. за месечните авансови вноски: А = Б - (В + 0,25 x В) … Б е дължимият годишен корпоративен данък; В е общият размер на определените месечни авансови вноски за годината; 2. за тримесечните авансови вноски: А = 0,75 x Б - (В + 0,25 x В) … В е общият размер на определените тримесечни авансови вноски за годината.' },
      { ref: 'чл. 89, ал. 4 ЗКПО', text: '1. … за първо, второ и трето тримесечие - за авансовите вноски преди подаване на декларацията за промени … по чл. 88; 2. намалените или увеличени … определени с декларацията по чл. 88 - за авансовите вноски след подаване на декларацията' },
      { ref: 'чл. 89, ал. 5 ЗКПО', text: 'Лихвата по ал. 1 се определя съгласно Закона за лихвите върху данъци, такси и други подобни държавни вземания и се изчислява от 16 април до 31 декември на съответната година …' },
      { ref: 'чл. 90, ал. 1 ЗКПО', text: '1. за месеците януари, февруари и март - в срок до 15 април …; 2. за месеците от април до ноември - в срок до 15-о число на месеца, за който се отнасят; 3. за месец декември - в срок до 1 декември' },
      { ref: 'чл. 90, ал. 2 ЗКПО', text: 'за първото и второто тримесечие се внасят до 15-о число на месеца, следващ тримесечието …, а за третото тримесечие - в срок до 1 декември. За четвъртото тримесечие не се прави тримесечна авансова вноска.' },
    ],
  });
}
